import {
  attr,
  boolean,
  float,
  id,
  integer,
  json,
  model,
  string,
} from "@moirei/dobby";
import type { Dayjs } from "dayjs";
import { cloneDeep } from "@apollo/client/utilities";
import { omitBy, isNil } from "lodash";
import ProductType from "./ProductType";
import ProductVariant from "./ProductVariant";
import {
  Model,
  datetime,
  ProductKind,
  type ProductAvailability,
  type WeightUnit,
  type ServicePeriodType,
  type BookingConfirmation,
  type ServiceSchedulingType,
  type PublishedStatus,
  type PublishableStatus,
  OnboardStep,
  Pricing as PricingModel,
  type IProductOption,
  type IProduct,
  savesInContext,
} from "@/layers/admin/models";
import SalesChannel from "./SalesChannel";
import Inventory from "./Inventory";
import { creatingProduct, updatingProduct } from "./hooks/product-hooks";
import { IntervalLimit, ServiceSchedule } from "./Service";
import TeamMember from "./TeamMember";
import { pricingDefaults } from "~/layers/admin/helpers/defaults";
import {
  availableVariants,
  variantOptionsHash,
} from "~/layers/admin/helpers/product";
import PublishableResource from "./PublishableResource";
import PublishedItem from "./PublishedItem";
import { Media } from "../../layers/admin/models/dobby/Media";
import ResourceFaq from "./ResourceFaq";
import { OrderForm } from "./OrderForm";

export type ProductPricingAttribute =
  | "pricing"
  | "comparison_pricing"
  | "whole_sale_pricing";

export class Product extends PublishableResource implements IProduct {
  static entity = "Product";
  static primaryKey = "handle";
  static primaryKeys = ["id", "gid", "handle"];
  static queryAttributes = ["id", "handle", "name"];

  constructor(attributes = {}) {
    super({
      pricing: cloneDeep(pricingDefaults),
      comparison_pricing: cloneDeep(pricingDefaults),
      whole_sale_pricing: cloneDeep(pricingDefaults),
      // image: {},
      options: [],
      variants: [],
      inventory: {},
      booking_order_limits: {},
      booking_duration_limits: {},
      bookingSchedule: {},
      teamMembers: [],

      // for parent attributes
      gallery: [],
      tags: [],
      faqs: [],
      order_form: {},

      // omit nil values for defaults above
      ...omitBy(attributes, isNil),
    });
  }

  // Inherited from PublishableResource - must redefine with decorators for Dobby ORM
  @datetime() declare readonly created_at: Dayjs;
  @datetime() declare readonly updated_at: Dayjs;
  @id() declare readonly id: string;
  @id() declare readonly gid: string;
  @string() declare readonly handle: string;
  @string() declare readonly objectName: string;
  @string() declare name: string;
  @string() declare description?: string;
  @string() declare currency_code?: string;
  @string() declare status: PublishableStatus;
  @string() declare publishedStatus: PublishedStatus;
  @boolean() declare hasUnpublishedChanges: boolean;
  @boolean() declare private?: boolean;
  @string({ default: [], list: true }) declare tags: string[];
  @model(() => Media) declare image?: Media;
  @model(() => Media, { list: true }) declare gallery?: Media[];
  @string() declare video_url?: string;
  @string() declare purchase_action?: string;
  @model(() => ResourceFaq, { list: true }) declare faqs: ResourceFaq[];
  @model(() => OnboardStep, { list: true }) declare nextSteps?: OnboardStep[];
  @model(() => OrderForm)
  @savesInContext()
  declare order_form?: OrderForm;

  // Product-specific attributes
  @string() declare kind: ProductKind;
  @boolean() declare bookable?: boolean;
  @string() declare short_description?: string;
  @string() declare vendor?: string;
  @boolean() declare virtual?: boolean;
  @json() declare meta?: object;
  // @string({ default: [], list: true }) declare channels: string[]
  @string({ default: [], list: true }) declare channels: string[];
  @string() declare cancellation_policy?: string;

  // bookable
  @string() declare booking_timezone?: string;
  @string() declare booking_period_type?: ServicePeriodType;
  @string() declare booking_period_start_date?: string;
  @string() declare booking_period_end_date?: string;
  @integer() declare booking_period_days?: number;
  @boolean() declare booking_period_count_calendar_days?: boolean;
  @string() declare booking_confirmation?: BookingConfirmation;
  @boolean() declare booking_hide_calendar_notes?: boolean;
  @integer() declare booking_minimum_notice?: number;
  @integer() declare booking_before_buffer?: number;
  @integer() declare booking_after_buffer?: number;
  @boolean() declare booking_block_before_buffer?: boolean;
  @boolean() declare booking_block_after_buffer?: boolean;
  @integer() declare booking_orders_per_time_slot?: number;
  @string() declare booking_scheduling_type?: ServiceSchedulingType;
  @integer() declare booking_slot_interval?: number;
  @string() declare booking_schedule_id?: string;
  @model(() => IntervalLimit) declare booking_order_limits?: IntervalLimit;
  @model(() => IntervalLimit) declare booking_duration_limits?: IntervalLimit;
  @model(() => TeamMember, { list: true }) declare teamMembers: TeamMember[];
  @model(() => PublishedItem, { list: true })
  declare publications?: PublishedItem[];

  @string() declare template_suffix?: string;

  // Default variant
  @float() declare cost_per_item?: number;
  @boolean() declare taxable?: boolean;
  @string() declare availability?: ProductAvailability;
  @boolean({ default: false }) declare enable_comparison_pricing: boolean;
  @float() declare weight?: number;
  @string() declare weight_unit?: WeightUnit;
  @string() declare fulfillment_service?: string;
  @string() declare inventory_management?: string;
  @integer() declare booking_duration?: number;
  @float() declare booking_cancellation_fee?: number;
  @model(() => PricingModel) declare pricing?: PricingModel;
  @model(() => PricingModel) declare comparison_pricing?: PricingModel;
  @model(() => PricingModel) declare whole_sale_pricing?: PricingModel;
  @model(() => Inventory) declare inventory?: Inventory;

  // relationships
  @model(() => ProductType) declare type?: ProductType;
  // @model(() => ProductCategories, { list: true }) categories?: ProductCategories[]
  // @model(() => ProductCollection, { list: true }) collections?: ProductCollection[]
  @model(() => ProductOption, { default: [], list: true })
  declare options: ProductOption[];
  @model(() => ProductVariant, { default: [], list: true })
  declare variants: ProductVariant[];
  @model(() => Product) declare parent?: Product;
  @model(() => Product, { list: true }) declare children?: Product[];
  @model(() => SalesChannel, { list: true })
  declare SalesChannel?: SalesChannel[];
  @model(() => ServiceSchedule) declare bookingSchedule?: ServiceSchedule;

  $isVariant(): this is ProductVariant {
    return false;
  }

  $isStandardKind() {
    return this.$getAttribute("kind") === ProductKind.STANDARD;
  }

  $isCompositeKind() {
    return this.$getAttribute("kind") === ProductKind.COMPOSITE;
  }

  $isVariationKind() {
    return this.$getAttribute("kind") === ProductKind.VARIATION;
  }

  $keepChanges(): void | false {
    super.$keepChanges();
    if (this.pricing) {
      this.pricing.$keepChanges();
    }
    if (this.comparison_pricing) {
      this.comparison_pricing.$keepChanges();
    }
    if (this.whole_sale_pricing) {
      this.whole_sale_pricing.$keepChanges();
    }
    if (this.inventory) {
      this.inventory.$keepChanges();
    }
    if (this.options && this.options.length) {
      this.options.forEach((option) => option.$keepChanges());
    }
    if (this.variants && this.variants.length) {
      this.variants.forEach((variant) => variant.$keepChanges());
    }
  }

  $variantOptionsHash(): string[] {
    return variantOptionsHash(this);
  }

  $availableVariants() {
    return availableVariants(this);
  }

  $getDataForCreate() {}

  $getDataForUpdate() {
    const data: Record<string, any> = this.$getAttributeChanges();

    return data;
  }

  /**
   * Duplicate this product with optional modifications
   */
  async $duplicate(data?: Record<string, any>) {
    const newProduct = await this.$newQuery()
      .select("*")
      .where({
        where: {
          type: "ProductWhereUniqueInput",
          required: true,
          value: { handle: this.handle },
        },
        data: {
          type: "WsProductUpdateInput",
          required: false,
          value: data || {},
        },
      })
      .parseWith((response) => response.data.wsDuplicateProduct)
      .mutate("wsDuplicateProduct");

    return new Product(newProduct);
  }

  static hooks() {
    return {
      $creating: creatingProduct,
      $updating: updatingProduct,
    };
  }
}

export class ProductOption extends Model implements IProductOption {
  static entity = "ProductOption";
  constructor(attributes = {}) {
    super({
      values: [],
      ...omitBy(attributes, isNil), // omit nil values for defaults above
    });
  }

  @attr() declare id: string;
  @string() declare name: string;
  @integer() declare position: number;
  @string({ default: [], list: true }) declare values: string[];
  // @model(() => Product) product: Product
}

export default Product;
